import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  CreateDMOStaffPayload,
  DMOStaff,
  DMOStaffListQueryParams,
  UpdateDMOStaffPayload,
} from '@/types/dmo-staff-management.types';

interface RawDMOStaffPaginatedResponse
  extends PaginatedApiResponse<DMOStaff[]> {
  total_items?: number;
  total_pages?: number;
  current_page?: number;
}

export const DMOStaffManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDMOStaffs: builder.query<
      PaginatedApiResponse<DMOStaff[]>,
      DMOStaffListQueryParams
    >({
      query: (params) => ({
        url: 'dmo-registration/staffs',
        method: 'GET',
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
        },
      }),
      transformResponse: (
        response: RawDMOStaffPaginatedResponse
      ): PaginatedApiResponse<DMOStaff[]> => {
        return {
          ...response,
          totalItems: response.totalItems ?? response.total_items,
          totalPages: response.totalPages ?? response.total_pages,
          currentPage: response.currentPage ?? response.current_page ?? 1,
        };
      },
      providesTags: ['DMOStaffs'],
      keepUnusedDataFor: 0,
    }),

    createDMOStaff: builder.mutation<
      ApiResponse<DMOStaff>,
      CreateDMOStaffPayload
    >({
      query: (data) => ({
        url: 'dmo-registration/staffs',
        method: 'POST',
        data,
      }),
      transformResponse: (response: ApiResponse<DMOStaff>) => response,
      invalidatesTags: ['DMOStaffs'],
    }),

    updateDMOStaff: builder.mutation<
      ApiResponse<DMOStaff>,
      UpdateDMOStaffPayload
    >({
      query: ({ id, data }) => ({
        url: `dmo-registration/staffs/${id}`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: ApiResponse<DMOStaff>) => response,
      invalidatesTags: ['DMOStaffs'],
    }),

    deleteDMOStaff: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `dmo-registration/staffs/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ['DMOStaffs'],
    }),
  }),
});

export const {
  useGetDMOStaffsQuery,
  useCreateDMOStaffMutation,
  useUpdateDMOStaffMutation,
  useDeleteDMOStaffMutation,
} = DMOStaffManagementService;
